/**
 * FRONT-2594 x FRONT-2600 — `artifact-updated` on the user channel: Eddy (or a
 * restore) changed a document, deck or sketch that may be open in the Canvas.
 *
 * Payload (src/artifacts/events.ts): `{ kind: "document"|"deck"|"sketch", id,
 * updatedAt (ISO), by: "eddy"|"person" }`, where `id` is the document's filename
 * or the deck/sketch slug — the same ids the Canvas item model uses.
 *
 * `planArtifactUpdate` is the pure decision; `applyArtifactUpdate` carries it out.
 * Flag off: nothing happens at all (the Canvas does not exist, so there is nothing
 * to refresh).
 */
import type { QueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { documentKeys } from '~/hooks/queries/use-documents';
import { artifactChangeKeys } from '~/hooks/queries/use-artifact-changes';

export type ArtifactUpdatePlan =
  | { action: 'none' }
  | { action: 'invalidate-document'; id: string }
  | { action: 'bump-frame'; kind: 'deck' | 'sketch'; id: string; updatedAt: string };

export function planArtifactUpdate(payload: unknown, flagOn: boolean): ArtifactUpdatePlan {
  if (!flagOn || !payload || typeof payload !== 'object' || Array.isArray(payload)) return { action: 'none' };
  const p = payload as Record<string, unknown>;
  const { kind, id, updatedAt } = p;
  if (typeof id !== 'string' || id.length === 0) return { action: 'none' };
  if (kind === 'document') return { action: 'invalidate-document', id };
  if ((kind === 'deck' || kind === 'sketch') && typeof updatedAt === 'string' && updatedAt.length > 0) {
    return { action: 'bump-frame', kind, id, updatedAt };
  }
  return { action: 'none' };
}

// --- iframe cache-buster ---------------------------------------------------
// The version an `artifact-updated` event announced for a deck or sketch. The
// frame folds it into its `?v=` so a new version reloads the iframe.

const frameVersions = new Map<string, string>();
const listeners = new Set<() => void>();

const frameVersionKey = (kind: string, id: string) => `${kind}:${id}`;

export function setFrameVersion(kind: 'deck' | 'sketch', id: string, updatedAt: string): void {
  if (frameVersions.get(frameVersionKey(kind, id)) === updatedAt) return;
  frameVersions.set(frameVersionKey(kind, id), updatedAt);
  for (const l of listeners) l();
}

export function getFrameVersion(kind: 'deck' | 'sketch', id: string): string | null {
  return frameVersions.get(frameVersionKey(kind, id)) ?? null;
}

/** Test seam: forget every announced version. */
export function resetFrameVersions(): void {
  frameVersions.clear();
  for (const l of listeners) l();
}

export function useFrameVersion(kind: 'deck' | 'sketch', id: string): string | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => getFrameVersion(kind, id),
    () => null,
  );
}

/**
 * The frame's cache key. When an `artifact-updated` event announced a version for
 * this item, that version ALONE is the key: Eddy's revision usually arrives twice
 * (the event, then the same turn's tool part carrying its own newer `updatedAt`),
 * and a key built from both would reload the iframe twice for one revision. The
 * tool output's own key is used only while nothing has been announced.
 */
export function frameCacheKey(base: string | number, announced: string | null): string | number {
  return announced ?? base;
}

export function applyArtifactUpdate(queryClient: QueryClient, payload: unknown, flagOn: boolean): ArtifactUpdatePlan {
  const plan = planArtifactUpdate(payload, flagOn);
  if (plan.action === 'invalidate-document') {
    void queryClient.invalidateQueries({ queryKey: documentKeys.detail(plan.id) });
    // FRONT-2669: the Changes view's answer describes the LAST edit, which this event just replaced.
    void queryClient.invalidateQueries({ queryKey: artifactChangeKeys.documentLastChangesFor(plan.id) });
  } else if (plan.action === 'bump-frame') {
    setFrameVersion(plan.kind, plan.id, plan.updatedAt);
  }
  return plan;
}
